"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";

function extractYoutubeId(input) {
	if (!input) return "";
	if (/^[a-zA-Z0-9_-]{11}$/.test(input)) return input;

	try {
		const url = new URL(input);

		if (url.hostname.includes("youtu.be")) {
			const id = url.pathname.replace("/", "").trim();
			return id;
		}

		const v = url.searchParams.get("v");
		if (v) return v;

		const shortsMatch = url.pathname.match(/\/shorts\/([a-zA-Z0-9_-]{11})/);
		if (shortsMatch?.[1]) return shortsMatch[1];

		const embedMatch = url.pathname.match(/\/embed\/([a-zA-Z0-9_-]{11})/);
		if (embedMatch?.[1]) return embedMatch[1];
	} catch {}

	return "";
}

function youtubeThumbCandidates(id) {
	return [
		`https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
		`https://i.ytimg.com/vi/${id}/sddefault.jpg`,
		`https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
	];
}

function ThumbImage({ videoId, alt, className }) {
	const candidates = useMemo(() => youtubeThumbCandidates(videoId), [videoId]);
	const [idx, setIdx] = useState(0);

	useEffect(() => {
		setIdx(0);
	}, [videoId]);

	return (
		// eslint-disable-next-line @next/next/no-img-element
		<img
			className={className}
			src={candidates[idx]}
			alt={alt}
			loading="lazy"
			decoding="async"
			draggable="false"
			onError={() => {
				// fallback do kolejnego wariantu jakości
				if (idx < candidates.length - 1) setIdx(idx + 1);
			}}
		/>
	);
}

function IconChevronLeft(props) {
	return (
		<svg
			width="20"
			height="16"
			viewBox="0 0 20 16"
			fill="none"
			aria-hidden="true"
			focusable="false"
			{...props}
		>
			<path
				d="M7.83594 14.835L0.835938 7.83496M0.835938 7.83496L7.83594 0.834961M0.835938 7.83496L18.8359 7.83496"
				stroke="#0377BA"
				strokeWidth="1.67"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function IconChevronRight(props) {
	return (
		<svg
			width="20"
			height="16"
			viewBox="0 0 20 16"
			fill="none"
			aria-hidden="true"
			focusable="false"
			{...props}
		>
			<path
				d="M11.8359 0.834961L18.8359 7.83496M18.8359 7.83496L11.8359 14.835M18.8359 7.83496L0.835938 7.83496"
				stroke="#0377BA"
				strokeWidth="1.67"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function IconPlay(props) {
	return (
		<svg
			width="80"
			height="80"
			viewBox="0 0 80 80"
			fill="none"
			aria-hidden="true"
			focusable="false"
			{...props}
		>
			<circle cx="40" cy="40" r="39" stroke="white" strokeWidth="2" />
			<path
				d="M57.332 36.5362C59.9987 38.0758 59.9987 41.9248 57.332 43.4644L34.332 56.7435C31.6654 58.2831 28.332 56.3586 28.332 53.2794L28.332 26.7213C28.332 23.6421 31.6654 21.7176 34.332 23.2572L57.332 36.5362Z"
				fill="white"
			/>
		</svg>
	);
}

export default function YoutubeSlider({
	title = "",
	items = [],
	className = "",
	cardAspect = "16/9",
	cardWidth = 520,
	openInModal = true,
	cssScope = "ys",
}) {
	const trackRef = useRef(null);
	const [active, setActive] = useState(null); // {id, title}
	const [canLeft, setCanLeft] = useState(false);
	const [canRight, setCanRight] = useState(true);

	const normalizedItems = useMemo(() => {
		return (items || [])
			.map((it) => {
				const id = it.youtubeId || it.id || extractYoutubeId(it.url);
				if (!id) return null;
				return {
					id,
					title: it.title || "",
					url: it.url || `https://www.youtube.com/watch?v=${id}`,
					badge: it.badge || "",
				};
			})
			.filter(Boolean);
	}, [items]);

	function updateArrows() {
		const el = trackRef.current;
		if (!el) return;
		const maxScroll = el.scrollWidth - el.clientWidth;
		setCanLeft(el.scrollLeft > 4);
		setCanRight(el.scrollLeft < maxScroll - 4);
	}

	useEffect(() => {
		updateArrows();
		const el = trackRef.current;
		if (!el) return;

		const onScroll = () => updateArrows();
		const onResize = () => updateArrows();

		el.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onResize);

		return () => {
			el.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onResize);
		};
	}, [normalizedItems.length]);

	function scrollByCards(dir) {
		const el = trackRef.current;
		if (!el) return;

		const firstCard = el.querySelector(`.${cssScope}-card`);
		const cardW = firstCard ? firstCard.getBoundingClientRect().width : 520;
		const gap = 18;
		el.scrollBy({ left: dir * (cardW + gap), behavior: "smooth" });
	}

	function onCardClick(it) {
		if (!openInModal) {
			window.open(it.url, "_blank", "noopener,noreferrer");
			return;
		}
		setActive({ id: it.id, title: it.title });
	}

	function closeModal() {
		setActive(null);
	}

	useEffect(() => {
		function onKey(e) {
			if (e.key === "Escape") closeModal();
		}
		if (active) window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [active]);

	useEffect(() => {
		if (!active) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [active]);

	const wrapClass = `${cssScope}-wrap ${className}`.trim();

	return (
		<section className={wrapClass}>
			<div className={`${cssScope}-header`}>
				{title ? <h3 className="pd-panel__title">{title}</h3> : <div />}

				<div className={`${cssScope}-nav`}>
					<button
						type="button"
						className={`${cssScope}-navBtn`}
						onClick={() => scrollByCards(-1)}
						disabled={!canLeft}
						aria-label="Poprzednie"
					>
						<IconChevronLeft className={`${cssScope}-navIcon`} />
					</button>

					<button
						type="button"
						className={`${cssScope}-navBtn`}
						onClick={() => scrollByCards(1)}
						disabled={!canRight}
						aria-label="Następne"
					>
						<IconChevronRight className={`${cssScope}-navIcon`} />
					</button>
				</div>
			</div>

			<div
				ref={trackRef}
				className={`${cssScope}-track`}
				style={{
					["--ys-cardWidth"]: `${cardWidth}px`,
					["--ys-cardAspect"]: cardAspect,
				}}
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "ArrowLeft") scrollByCards(-1);
					if (e.key === "ArrowRight") scrollByCards(1);
				}}
				aria-label="Slider wideo"
			>
				{normalizedItems.map((it) => (
					<article key={it.id} className={`${cssScope}-card`}>
						<button
							type="button"
							className={`${cssScope}-thumbBtn`}
							onClick={() => onCardClick(it)}
							aria-label={`Odtwórz: ${it.title}`}
						>
							<div className={`${cssScope}-thumb`}>
								<ThumbImage
									videoId={it.id}
									alt={it.title || "Miniatura wideo"}
									className={`${cssScope}-thumbImg`}
								/>

								<div className={`${cssScope}-play`}>
									<IconPlay className={`${cssScope}-playIcon`} />
								</div>

								{it.badge ? <div className={`${cssScope}-badge`}>{it.badge}</div> : null}
							</div>
						</button>

						<button
							type="button"
							className={`${cssScope}-caption`}
							onClick={() => onCardClick(it)}
							title={it.title}
						>
							{it.title}
						</button>
					</article>
				))}
			</div>

			{openInModal && active ? (
				<div
					className={`${cssScope}-modalOverlay`}
					role="dialog"
					aria-modal="true"
					aria-label="Odtwarzacz wideo"
				>
					<button
						type="button"
						className={`${cssScope}-modalBackdrop`}
						onClick={closeModal}
						aria-label="Zamknij"
					/>

					<div className={`${cssScope}-modalContent`}>
						<button
							type="button"
							className={`${cssScope}-modalCloseButton`}
							onClick={closeModal}
							aria-label="Zamknij"
						/>

						<div className={`${cssScope}-modalHeader`}>
							<h3 className={`${cssScope}-modalTitle`} title={active.title}>
								{active.title}
							</h3>
						</div>

						<div className={`${cssScope}-modalBody`}>
							<iframe
								className={`${cssScope}-iframe`}
								src={`https://www.youtube-nocookie.com/embed/${active.id}?autoplay=1&rel=0`}
								title={active.title || "Wideo"}
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
								allowFullScreen
							/>
						</div>
					</div>
				</div>
			) : null}
		</section>
	);
}
